import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DownloadSimpleIcon, XIcon } from '@phosphor-icons/react';

import { PageHeader } from '../../../components/PageHeader';
import { Spinner } from '../../../components/Spinner';
import { TimeFrame } from '../../../components/time-frame/time-frame';
import { TimeFrameState, TimeFrameType } from '../../../components/time-frame/types';
import { ITableHeader, Table } from '../../../components/table/Table';
import { OrderComplaintType } from 'src/types/enums';
import { useComplaints, type Complaint } from '../models';
import { formatDate } from '../../../utils/date';
import { PageHeading } from 'components/PageHeading';
import { Breadcrumb } from 'components/Breadcrumb';
import { SearchType, SearchWithType } from 'components/search-with-type/SearchWithType';
import { useSearchParam } from 'src/hooks/useSearchParam';
import { SimpleSelect } from 'components/select/SimpleSelect';
import { useTranslation } from 'contexts/translation-context';
import { Button } from '../../../components/button/Button';
import { Pagination } from '../../../components/pagination/Pagination';
import { FilterField, FilterPanel, ListPage } from 'components/page-templates/ListPage';
import { exportComplaintsToExcel } from './export-complaints';

const PAGE_SIZE = 50;

const COMPLAINTS_HEADERS: ITableHeader[] = [
  {
    id: 'type',
    name: 'Type',
  },
  {
    id: 'content',
    name: 'Inhoud',
    width: '300px',
  },
  {
    id: 'orderNumber',
    name: 'Order Nr',
  },
  {
    id: 'customer',
    name: 'Klant',
    width: '200px',
  },
  {
    id: 'customerVat',
    name: 'BTW Nr',
  },
  {
    id: 'supplier',
    name: 'Vervoerder',
    width: '200px',
  },
  {
    id: 'createdAt',
    name: 'Datum',
  },
];

const searchTypes: SearchType[] = [
  {
    label: 'Klant naam',
    value: 'customerName',
  },
  {
    label: 'Vervoerder naam',
    value: 'supplierName',
  },
];

export const ComplaintsPage = () => {
  const [timeFrame, setTimeFrame] = useSearchParam<TimeFrameState | null>('timeframe', null);
  const [search, setSearch] = useSearchParam('search', {
    value: '',
    type: searchTypes[0].value,
  });
  const [selectedComplaintType, setSelectedComplaintType] = useSearchParam<OrderComplaintType | null>('type', null);
  const [pageIndex, setPageIndex] = useState(0);

  const { i18n } = useTranslation();
  const complaintOptions = useMemo(() => {
    return Object.values(OrderComplaintType).map((type: OrderComplaintType) => ({
      key: type,
      name: i18n(`orderComplaintType.${type}`),
    }));
  }, [i18n]);

  const { data, error, isLoading, mutate } = useComplaints(timeFrame?.startDate, timeFrame?.endDate);

  // `timeFrame` is only set once TimeFrame's mount effect fires, so until then
  // there is no SWR request yet and the list is still on its way.
  const isFetching = isLoading || !timeFrame;

  const customerFilter = search.type === 'customerName' ? search.value : '';
  const supplierFilter = search.type === 'supplierName' ? search.value : '';

  const filteredComplaints = useMemo(() => {
    return (data ?? []).filter((complaint) => {
      const customerName = complaint.order?.customer?.name?.toLowerCase() || '';
      const supplierName = complaint.order?.supplier?.name?.toLowerCase() || '';

      const matchesCustomer = customerFilter === '' || customerName.includes(customerFilter.toLowerCase());
      const matchesSupplier = supplierFilter === '' || supplierName.includes(supplierFilter.toLowerCase());
      const matchesComplaintType = selectedComplaintType === null || complaint.type === selectedComplaintType;

      return matchesCustomer && matchesSupplier && matchesComplaintType;
    });
  }, [data, customerFilter, supplierFilter, selectedComplaintType]);

  // Filtering happens client-side, so a filter change can shrink the result set
  // below the current page: always go back to the first page.
  useEffect(() => {
    setPageIndex(0);
  }, [customerFilter, supplierFilter, selectedComplaintType, timeFrame?.startDate, timeFrame?.endDate]);

  const pageCount = Math.max(1, Math.ceil(filteredComplaints.length / PAGE_SIZE));
  const currentPage = Math.min(pageIndex, pageCount - 1);
  const pageStart = currentPage * PAGE_SIZE;
  const visibleComplaints = filteredComplaints.slice(pageStart, pageStart + PAGE_SIZE);

  const handleExportExcel = (complaints: Complaint[]) => {
    if (timeFrame) {
      exportComplaintsToExcel(complaints, timeFrame);
    }
  };

  const activeFilterCount = [search.value.trim(), selectedComplaintType].filter(Boolean).length;

  return (
    <>
      <PageHeader title="Klachten" />

      <ListPage
        heading={
          <PageHeading
            noMarginBottom
            leftSide={<Breadcrumb currentItem="Klachten" />}
            rightSide={
              <Button
                color="secondary"
                iconLeft={<DownloadSimpleIcon className="button-icon" />}
                onTrigger={() => handleExportExcel(filteredComplaints)}
                isDisabled={filteredComplaints.length === 0}
              >
                Exporteer naar Excel
              </Button>
            }
          />
        }
        filters={
          <FilterPanel
            activeCount={activeFilterCount}
            actions={
              <div className="flex items-center gap-2">
                {isFetching && <Spinner size={8} />}
                <Button
                  size={8}
                  color="secondary"
                  iconLeft={<XIcon className="button-icon" />}
                  onTrigger={() => {
                    setSearch({ value: '', type: searchTypes[0].value });
                    setSelectedComplaintType(null);
                  }}
                >
                  Wis alle filters
                </Button>
              </div>
            }
          >
            <FilterField label="Zoek">
              <SearchWithType
                value={search.value}
                onChange={(val) => setSearch((prev) => ({ ...prev, value: val }))}
                types={searchTypes}
                selectedType={search.type}
                onTypeChange={(val) => setSearch((prev) => ({ ...prev, type: val }))}
              />
            </FilterField>

            <FilterField label="Type klacht">
              <SimpleSelect
                selectedItem={complaintOptions.find((option) => option.key === selectedComplaintType) || null}
                items={[...complaintOptions]}
                onSelect={(val) => {
                  setSelectedComplaintType(val?.key as OrderComplaintType | null);
                }}
                isOptional={true}
              />
            </FilterField>

            <div className="col-span-full">
              <TimeFrame onChange={setTimeFrame} defaultType={TimeFrameType.Month} />
            </div>
          </FilterPanel>
        }
        summary={`Totaal aantal klachten: ${filteredComplaints.length}`}
        pagination={
          <Pagination
            hasPrevious={currentPage > 0}
            previous={() => setPageIndex(currentPage - 1)}
            hasNext={currentPage < pageCount - 1}
            next={() => setPageIndex(currentPage + 1)}
            isFetching={isFetching}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={COMPLAINTS_HEADERS}
          data={visibleComplaints}
          error={error}
          onRetry={() => mutate()}
          isLoading={isFetching}
          emptyState={<span className="text-sm text-fg-muted">Geen klachten gevonden</span>}
          mapData={(complaint) => {
            return [
              complaint.type || '-',
              <div className="max-w-xs truncate" title={complaint.content}>
                {complaint.content}
              </div>,
              complaint.order ? (
                <Link to={`/internal/orders/${complaint.order.id}/complaints`} className="link-text whitespace-nowrap">
                  {complaint.order.orderNumber || 'DRAFT'}
                </Link>
              ) : (
                '-'
              ),
              complaint.order?.customer?.name || '-',
              complaint.order?.customer?.vatNumber || '-',
              complaint.order?.supplier?.name || '-',
              formatDate(complaint.createdAt),
            ];
          }}
        />
      </ListPage>
    </>
  );
};
